import * as z from 'zod';
import type { ZodString, ZodTypeAny } from 'zod';
import type { I18n } from '@shell/composables/useI18n';

export type Transform = (schema: ZodString) => ZodTypeAny;

// Untyped alias used for iterating factories; callers see the real signatures
// through FieldBuilder.
type TransformFactory = (...args: any[]) => Transform;

// Factories for validators that append a Transform to a field's pipeline. Each
// closes over the field's own key so its message names the field it belongs to,
// falling back to a generic message when the field has no key. Add new
// transform-style validators here. FieldBuilder and makeBuilder pick them up
// automatically. Validators must not accept a key override - a message that
// needs to read differently for a given field warrants its own translation key,
// not a different key passed at the call site.
function createTransformFactories(t: I18n['t'], fieldKey: string) {
  return {
    url: (): Transform => {
      return (s) => s.url(fieldKey ? t('validation.url', { key: t(fieldKey) }) : t('validation.genericUrl'));
    },
  };
}

type TransformFactories = ReturnType<typeof createTransformFactories>;

// NOTE: builder methods (required, url, and anything added to
// createTransformFactories) must be the last calls in a chain. Each is
// implemented by bolting extra methods onto a real Zod schema instance, so
// chaining a genuine ZodTypeAny method afterwards (.refine(), .optional(),
// .transform(), etc.) returns a plain Zod schema without these extras - any
// further .required()/.url() calls on that result will be undefined at
// runtime. If a field needs that kind of composition, apply it to the
// resulting z.object({...}) shape as a whole, or add a transform-style
// validator to createTransformFactories instead.
export type FieldBuilder = ZodTypeAny & {
  required(): FieldBuilder;
} & {
  [K in keyof TransformFactories]: (...args: Parameters<TransformFactories[K]>) => FieldBuilder;
};

export function zodValidators(t: I18n['t']) {
  const firstIssue = (schemas: ZodTypeAny[], v: string) => {
    return schemas
      .map((schema) => schema.safeParse(v))
      .find((result) => !result.success)
      ?.error
      .issues[0];
  };

  function buildSchema(key: string, transforms: Transform[], isRequired: boolean): ZodTypeAny {
    // Plain optional case (field() with no chained validators). Skip the
    // superRefine wrapper entirely since it would always be a no-op.
    if (transforms.length === 0 && !isRequired) {
      return z.preprocess((v) => (v ?? '').toString(), z.string());
    }

    // Compiled once per builder state, then reused across every superRefine
    // call (e.g. every keystroke revalidation) since none of these depend on
    // `v`.
    const compiledTransforms = transforms.map((transform) => transform(z.string()));
    const requiredSchema = isRequired ? z.string().refine((val) => val.trim().length > 0, t('validation.required', { key: t(key) })) : undefined;

    return z.preprocess(
      (v) => (v ?? '').toString(),
      z.string().superRefine((v: string, ctx) => {
        const requiredResult = requiredSchema?.safeParse(v);
        const hasIssue = requiredResult?.success === false ? requiredResult.error.issues[0] : (!v ? undefined : firstIssue(compiledTransforms, v));

        if (hasIssue) {
          ctx.addIssue(hasIssue);
        }
      })
    );
  }

  function makeBuilder(key: string, transforms: Transform[], isRequired: boolean): FieldBuilder {
    const schema = buildSchema(key, transforms, isRequired);
    const factories = Object.entries(createTransformFactories(t, key)) as [string, TransformFactory][];

    const transformMethods = Object.fromEntries(factories.map(([name, factory]) => [
      name,
      (...args: unknown[]) => makeBuilder(key, [...transforms, factory(...args)], isRequired),
    ]));

    return Object.assign(
      schema,
      transformMethods,
      { required: () => makeBuilder(key, transforms, true) }
    ) as unknown as FieldBuilder;
  }

  function field(key = ''): FieldBuilder {
    return makeBuilder(key, [], false);
  }

  return { field };
}
